import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanel, SurveyContentPanelInset } from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import {
  readSetAProfileAnswers,
  SET_A_AREA_MAX_LENGTH,
  SET_A_RACES,
  SET_A_STATES,
  isValidEmail,
  writeSetAProfileAnswers,
} from '@/lib/setAProfileStorage';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const fieldInputClass =
  'min-w-0 w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-300';

const selectClass =
  'min-w-0 w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-300';

const labelClass = 'm-0 text-left text-[16px] font-medium text-white sm:text-[17px]';

function FieldStack({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className={labelClass}>
        {label}
      </label>
      {children}
    </div>
  );
}

export default function SetAProfile() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [name, setName] = useState(() => readSetAProfileAnswers()?.name ?? '');
  const [email, setEmail] = useState(() => readSetAProfileAnswers()?.email ?? '');
  const [race, setRace] = useState(() => readSetAProfileAnswers()?.race ?? '');
  const [state, setState] = useState(() => readSetAProfileAnswers()?.state ?? '');
  const [area, setArea] = useState(() => readSetAProfileAnswers()?.area ?? '');
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    writeSetAProfileAnswers({ name, email, race, state, area });
  }, [name, email, race, state, area]);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!customerUuid || !otpVerified) {
    return <Navigate to="/phone" replace />;
  }
  if (questionnaireSet === 'B') {
    return <Navigate to="/ldproductnow" replace />;
  }
  if (questionnaireSet !== 'A') {
    return <Navigate to="/survey" replace />;
  }

  const handleContinue = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedRace = race.trim();
    const trimmedState = state.trim();
    const trimmedArea = area.trim();

    if (!trimmedName) {
      setWarning(t.setAProfile.nameWarning);
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setWarning(t.setAProfile.emailWarning);
      return;
    }
    if (!trimmedRace) {
      setWarning(t.setAProfile.raceWarning);
      return;
    }
    if (!trimmedState) {
      setWarning(t.setAProfile.stateWarning);
      return;
    }
    if (!trimmedArea) {
      setWarning(t.setAProfile.areaWarning);
      return;
    }
    if (trimmedArea.length > SET_A_AREA_MAX_LENGTH) {
      setWarning(t.setAProfile.areaLengthWarning);
      return;
    }

    writeSetAProfileAnswers({
      name: trimmedName,
      email: trimmedEmail,
      race: trimmedRace,
      state: trimmedState,
      area: trimmedArea,
    });
    setWarning(null);
    navigate('/survey/prevbrand');
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto pb-12">
        <div className="mx-auto mt-[7vh] flex w-full max-w-full flex-col py-6">
          <SurveyContentPanelInset>
            <SurveyContentPanel className="!p-4">
              <div className="flex flex-col gap-6">
                <FieldStack label={t.setAProfile.nameLabel} htmlFor="set-a-name">
                  <input
                    id="set-a-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={fieldInputClass}
                  />
                </FieldStack>

                <FieldStack label={t.setAProfile.emailLabel} htmlFor="set-a-email">
                  <input
                    id="set-a-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={fieldInputClass}
                  />
                </FieldStack>

                <FieldStack label={t.setAProfile.raceLabel} htmlFor="set-a-race">
                  <select
                    id="set-a-race"
                    value={race}
                    onChange={(e) => setRace(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">{t.setAProfile.selectRace}</option>
                    {SET_A_RACES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </FieldStack>

                <FieldStack label={t.setAProfile.stateLabel} htmlFor="set-a-state">
                  <select
                    id="set-a-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">{t.setAProfile.selectState}</option>
                    {SET_A_STATES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </FieldStack>

                <FieldStack label={t.setAProfile.areaLabel} htmlFor="set-a-area">
                  <div className="min-w-0">
                    <input
                      id="set-a-area"
                      type="text"
                      maxLength={SET_A_AREA_MAX_LENGTH}
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      className={fieldInputClass}
                    />
                    <p className="m-0 mt-1 text-right text-[12px] text-white/80">
                      {area.length}/{SET_A_AREA_MAX_LENGTH}
                    </p>
                  </div>
                </FieldStack>
              </div>
            </SurveyContentPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton onClick={handleContinue}>{t.setAProfile.continue}</SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
